import React from 'react';
import PriceChartWidget from './PriceChartWidget';
import AdvancedPriceChartWidget from './AdvancedPriceChartWidget';
import { INDICATOR_DEFAULTS } from '../../utils/indicatorDefaults';

// Widget-specific error boundary
class WidgetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`Widget ${this.props.widgetId} error:`, error);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Error info:', errorInfo);
    console.error('Component stack:', errorInfo.componentStack);

    let errorMessage = 'Widget failed to load';

    // Provide specific error messages with more details
    if (error.message?.includes("Cannot read properties of null")) {
      errorMessage = `Chart data error - ${error.message}`;
      console.error('Full error for "Cannot read properties":', {
        message: error.message,
        stack: error.stack,
        widgetProps: this.props.widget
      });
    } else if (error.message?.includes("Cannot read properties of undefined")) {
      errorMessage = `Missing data - ${error.message}`;
    } else if (error.message?.includes("NaN")) {
      errorMessage = 'Invalid numerical data in chart';
    } else if (error.message?.includes("undefined")) {
      errorMessage = 'Missing required data for this chart';
    } else {
      errorMessage = `Error: ${error.message}`;
    }

    this.setState(prevState => ({
      errorMessage,
      errorCount: prevState.errorCount + 1
    }));
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      errorMessage: '',
      errorCount: 0
    });
  };

  handleRemoveIndicators = () => {
    if (this.props.onRemoveIndicators) {
      this.props.onRemoveIndicators();
      this.handleReset();
    }
  };

  render() {
    if (this.state.hasError) {
      const { widget } = this.props;

      return (
        <div className="h-full flex flex-col bg-gray-900 border border-red-900/50 rounded">
          {/* Error Header */}
          <div className="bg-red-900/20 border-b border-red-900/50 px-3 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-red-400 text-sm font-medium">
                  ⚠️ {widget?.ticker || 'Widget'} Error
                </span>
              </div>
              <button
                onClick={this.props.onDelete}
                className="text-red-400 hover:text-red-300 text-xs"
                title="Delete this widget"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Error Content */}
          <div className="flex-1 flex flex-col items-center justify-center p-4">
            <p className="text-red-300 text-sm text-center mb-4">
              {this.state.errorMessage}
            </p>

            {/* Helpful information */}
            <div className="bg-gray-800/50 rounded p-3 mb-4 max-w-sm">
              <p className="text-gray-400 text-xs mb-2">This often happens when:</p>
              <ul className="text-gray-500 text-xs space-y-1 list-disc list-inside">
                <li>Indicators (MACD, RSI, BB) have insufficient data</li>
                <li>The ticker has limited historical data</li>
                <li>Technical calculations produced invalid values</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 w-full max-w-xs">
              <button
                onClick={this.handleReset}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm transition"
              >
                🔄 Retry Loading
              </button>

              {widget?.config?.indicators?.length > 0 && (
                <button
                  onClick={this.handleRemoveIndicators}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-sm transition"
                >
                  📊 Remove Indicators & Retry
                </button>
              )}

              <button
                onClick={() => {
                  if (this.props.onEdit) {
                    this.props.onEdit();
                  }
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-sm transition"
              >
                ⚙️ Edit Widget Settings
              </button>
            </div>

            {/* Error count indicator */}
            {this.state.errorCount > 1 && (
              <p className="text-gray-500 text-xs mt-3">
                Failed {this.state.errorCount} times
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Safe widget wrapper component
function SafeWidgetWrapper({
  widget,
  onDelete,
  isLinked,
  crosshairDate,
  onCrosshairMove,
  onEdit,
  onUpdateWidget,
  showBollinger,
  setShowBollinger
}) {

  // Function to remove indicators from the widget
  const handleRemoveIndicators = async () => {
    if (onUpdateWidget && widget) {
      try {
        // Update the widget to remove indicators
        const updatedConfig = {
          ...widget.config,
          indicators: []
        };

        await onUpdateWidget(widget.id, {
          ...widget,
          config: updatedConfig
        });

        console.log(`Removed indicators from widget ${widget.id}`);
      } catch (error) {
        console.error('Failed to remove indicators:', error);
      }
    }
  };

  // Validate widget data
  if (!widget || !widget.ticker) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900 border border-gray-800 rounded">
        <div className="text-center p-4">
          <p className="text-gray-400 mb-2">Invalid Widget Configuration</p>
          <button
            onClick={onDelete}
            className="text-red-400 hover:text-red-300 text-sm underline"
          >
            Remove Widget
          </button>
        </div>
      </div>
    );
  }

  // Extract and validate configuration
  const config = widget.config || {};
  const dateRange = config.date_range || '1y';
  const indicators = Array.isArray(config.indicators) ? config.indicators : [];

  // Check if we should use the advanced widget (with indicators)
  const hasIndicators = indicators.length > 0;

  // Merge custom indicator settings with defaults
  const indicatorConfig = {};
  indicators.forEach(indicatorName => {
    const defaultSettings = INDICATOR_DEFAULTS[indicatorName] || {};
    const customSettings = config.indicator_settings?.[indicatorName] || {};

    // Merge settings, with custom overriding defaults
    indicatorConfig[indicatorName] = {
      ...defaultSettings,
      ...customSettings,
      // Special handling for colors to do deep merge
      colors: {
        ...defaultSettings.colors,
        ...customSettings.colors
      }
    };
  });

  return (
    <WidgetErrorBoundary
      widgetId={widget.id}
      widget={widget}
      onDelete={onDelete}
      onEdit={onEdit}
      onRemoveIndicators={handleRemoveIndicators}
    >
      <div className="h-full">
        {hasIndicators ? (
          <AdvancedPriceChartWidget
            ticker={widget.ticker}
            dateRange={dateRange}
            indicators={indicators}
            indicatorConfig={indicatorConfig}
            isLinked={isLinked}
            crosshairDate={crosshairDate}
            onCrosshairMove={onCrosshairMove}
            showBollinger={showBollinger}
            setShowBollinger={setShowBollinger}
          />
        ) : (
          <PriceChartWidget
            ticker={widget.ticker}
            dateRange={dateRange}
            isLinked={isLinked}
            crosshairDate={crosshairDate}
            onCrosshairMove={onCrosshairMove}
          />
        )}
      </div>
    </WidgetErrorBoundary>
  );
}

export default SafeWidgetWrapper;